// Flutter curves and durations (spec 01 header table, §3.8, §13) for `motion`
// and CSS. Easing arrays plug straight into `motion` transitions:
//   transition={{ duration: seconds.toastIn, ease: curves.easeOutBack }}

export type CubicBezier = readonly [number, number, number, number];
export type EasingFunction = (t: number) => number;

/** Flutter `Curves.*` as cubic-bezier control points. */
export const curves = {
  linear: [0, 0, 1, 1],
  ease: [0.25, 0.1, 0.25, 1],
  easeIn: [0.42, 0, 1, 1],
  easeOut: [0, 0, 0.58, 1],
  easeInOut: [0.42, 0, 0.58, 1],
  easeInCubic: [0.55, 0.055, 0.675, 0.19],
  easeOutCubic: [0.215, 0.61, 0.355, 1],
  easeInOutCubic: [0.645, 0.045, 0.355, 1],
  easeOutQuart: [0.165, 0.84, 0.44, 1],
  easeOutBack: [0.175, 0.885, 0.32, 1.275],
  easeInOutSine: [0.445, 0.05, 0.55, 0.95],
  /** Close cubic fit of `Curves.decelerate` (use `decelerate` for the exact function). */
  decelerate: [0.25, 0.46, 0.45, 0.94],
  /** `Easing.legacyDecelerate`: Material modal bottom sheets. */
  legacyDecelerate: [0, 0, 0.2, 1],
  fastOutSlowIn: [0.4, 0, 0.2, 1],
  linearToEaseOut: [0.35, 0.91, 0.33, 0.97],
  easeInToLinear: [0.67, 0.03, 0.65, 0.09],
} as const satisfies Record<string, CubicBezier>;

export type CurveName = keyof typeof curves;

/** Durations in milliseconds. */
export const durations = {
  quick: 180,
  standard: 220,
  emphasized: 320,
  theme: 200,
  tabSlide: 280,
  navPill: 300,
  navIconSwap: 200,
  routePush: 320,
  routePop: 240,
  sheetEnter: 250,
  sheetExit: 200,
  dialog: 150,
  menu: 300,
  quickMenu: 280,
  toastIn: 420,
  toastOut: 280,
  toastAuto: 2600,
  toastAutoError: 3200,
  swipeDismiss: 280,
  swipeSettle: 220,
  swipeMin: 120,
  playGlyph: 180,
  finishedBadge: 180,
  coverFadeIn: 250,
  coverPlaceholderFade: 1000,
  miniProgress: 500,
  skeletonShimmer: 1500,
  skeletonPulse: 1500,
  skeletonFade: 250,
  skeletonShowDelay: 200,
  skeletonMinDisplay: 400,
  contentReveal: 300,
  searchFocus: 180,
  expandSection: 220,
  expansionHeader: 300,
  blockingOverlay: 220,
  press: 100,
  pressRelease: 200,
  menuExit: 140,
  listRemove: 200,
  tooltipWait: 0,
  tooltipShow: 1500,
  tooltipFadeIn: 150,
  tooltipFadeOut: 75,
  longPress: 500,
} as const;

export type DurationName = keyof typeof durations;

/** The same durations in seconds (the unit `motion` expects). */
export const seconds = Object.fromEntries(Object.entries(durations).map(([k, v]) => [k, v / 1000])) as Record<
  DurationName,
  number
>;

/** Spring presets (mass / stiffness / damping as in the Flutter simulations). */
export const springs = {
  /** CupertinoSlidingSegmentedControl thumb (sliding_segmented_control.dart). */
  segmentedThumb: { type: 'spring', mass: 1, stiffness: 503.551, damping: 44.8799 },
  /** ScrollSpringSimulation default: mass 0.5, stiffness 100, damping ratio 1.1. */
  pageSettle: { type: 'spring', mass: 0.5, stiffness: 100, damping: 2 * 1.1 * Math.sqrt(100 * 0.5) },
} as const;

export const clamp01 = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Evaluates a cubic-bezier easing at `t` exactly as Flutter's `Cubic.transform`
 * (bisection on x). `t` is clamped to [0, 1]; the output may overshoot (easeOutBack).
 */
export function evaluateCubic([x1, y1, x2, y2]: CubicBezier, t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const point = (a: number, b: number, m: number) =>
    3 * a * (1 - m) * (1 - m) * m + 3 * b * (1 - m) * m * m + m * m * m;
  let start = 0;
  let end = 1;
  for (let i = 0; i < 64; i++) {
    const mid = (start + end) / 2;
    const estimate = point(x1, x2, mid);
    if (Math.abs(t - estimate) < 0.0001) return point(y1, y2, mid);
    if (estimate < t) start = mid;
    else end = mid;
  }
  return point(y1, y2, (start + end) / 2);
}

/** `Curves.decelerate`: 1 - (1 - t)^2. */
export const decelerate: EasingFunction = (t) => {
  const inv = 1 - clamp01(t);
  return 1 - inv * inv;
};

/** Returns an easing function for a curve (for driving derived values from a linear progress). */
export function easing(curve: CubicBezier | CurveName): EasingFunction {
  const bezier = typeof curve === 'string' ? curves[curve] : curve;
  if (bezier === curves.linear) return clamp01;
  return (t) => evaluateCubic(bezier, t);
}

/**
 * Flutter `Interval(begin, end, curve)`: maps a parent progress `t` to the
 * interval's local progress and applies the curve.
 */
export function interval(t: number, begin: number, end: number, curve?: CubicBezier | CurveName): number {
  const local = end <= begin ? (t >= end ? 1 : 0) : clamp01((t - begin) / (end - begin));
  return curve ? easing(curve)(local) : local;
}

/** Ready-made `motion` transitions for common Flutter motions. */
export const transitions = {
  sheetEnter: { duration: seconds.sheetEnter, ease: curves.legacyDecelerate },
  sheetExit: { duration: seconds.sheetExit, ease: curves.legacyDecelerate },
  dialogFade: { duration: seconds.dialog, ease: curves.easeOut },
  routePush: { duration: seconds.routePush, ease: curves.easeOutCubic },
  routePop: { duration: seconds.routePop, ease: curves.easeInCubic },
  tabSlide: { duration: seconds.tabSlide, ease: curves.easeOutCubic },
  quick: { duration: seconds.quick, ease: curves.easeOutCubic },
  standard: { duration: seconds.standard, ease: curves.easeOutCubic },
  emphasized: { duration: seconds.emphasized, ease: curves.easeOutCubic },
} as const;

/**
 * Scales a base duration by the remaining fraction of travel with a floor,
 * as SwipeDismissibleSheet does: clamp(round(base * f), min, base).
 */
export function scaledDuration(baseMs: number, fraction: number, minMs: number = durations.swipeMin): number {
  return Math.min(baseMs, Math.max(minMs, Math.round(baseMs * clamp01(fraction))));
}
